import type { RefObject } from "react";
import { useEffect, useRef } from "react";

/**
 * Drives a native `<dialog>` from an `open` flag: `showModal()` gives it the top
 * layer and a real backdrop, Escape arrives as the element's own `cancel` event,
 * and the browser restores focus to whatever was focused before it opened.
 *
 * Both callers render the element only while `open`, so the content inside
 * unmounts on close and a reopen starts from a clean state.
 */
export function useDialogModal(
  open: boolean,
  onClose: () => void,
): RefObject<HTMLDialogElement | null> {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    function handleCancel(event: Event): void {
      event.preventDefault();
      onClose();
    }
    dialog.addEventListener("cancel", handleCancel);
    return () => dialog.removeEventListener("cancel", handleCancel);
    // `open` is a dependency on purpose: the element only exists while open, so an
    // effect that ran once on mount (with a null ref) would never attach this
    // listener. Without it Escape closed the dialog natively while the caller's
    // state stayed open — the panel then lingered, closed and unbackdropped.
  }, [open, onClose]);

  return ref;
}
